import { useState, useEffect } from 'react';
import { Wifi, Gift, ShoppingBag, ArrowRight, Check, Star, Coffee, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function WifiWelcomePage() {
  const navigate = useNavigate();
  const [view, setView] = useState('welcome'); // 'welcome', 'form', 'rewards', 'loading'
  const [settings, setSettings] = useState({
    headline: 'Welcome to Bulaccino Free Wi-Fi',
    message: 'Join Bulaccino Rewards, order online, and receive cafe updates.',
    redirect_destination: 'order'
  });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    email_marketing_consent: false,
    sms_marketing_consent: false
  });
  const [error, setError] = useState('');
  const [rewardsStatus, setRewardsStatus] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Load settings
  useEffect(() => {
    fetch(`${API_URL}/api/wifi/settings`)
      .then(res => res.json())
      .then(data => setSettings(prev => ({ ...prev, ...data })))
      .catch(err => console.error('Failed to load settings:', err));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    // Validate
    if (!formData.name.trim()) {
      setError('Please enter your name');
      setIsSubmitting(false);
      return;
    }
    if (!formData.email.trim() || !formData.email.includes('@')) {
      setError('Please enter a valid email');
      setIsSubmitting(false);
      return;
    }
    if (!formData.phone.trim() || formData.phone.replace(/\D/g, '').length < 7) {
      setError('Please enter a valid phone number');
      setIsSubmitting(false);
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/wifi/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const data = await res.json();

      if (data.success) {
        setRewardsStatus(data.rewards_status);
        setView('rewards');
      } else {
        setError(data.detail || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      setError('Connection error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRedirect = (destination) => {
    if (destination === 'order') {
      navigate('/order');
    } else if (destination === 'rewards') {
      navigate('/loyalty');
    } else {
      navigate('/');
    }
  };

  // Welcome View
  if (view === 'welcome') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-amber-50 to-orange-50 flex flex-col items-center justify-center p-4" data-testid="wifi-welcome-page">
        {/* Logo */}
        <div className="mb-6">
          <img 
            src="/bulaccino-logo.svg" 
            alt="Bulaccino Cafe" 
            className="h-20 w-auto"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = '/logo.png';
            }}
          />
        </div>

        {/* WiFi Icon */}
        <div className="w-20 h-20 bg-amber-500 rounded-full flex items-center justify-center mb-6 shadow-lg">
          <Wifi className="w-10 h-10 text-white" />
        </div>

        {/* Headline */}
        <h1 className="text-2xl font-bold text-gray-900 text-center mb-3" data-testid="wifi-headline">
          {settings.headline}
        </h1>

        {/* Message */}
        <p className="text-gray-600 text-center mb-8 max-w-xs">
          {settings.message}
        </p>

        {/* Buttons */}
        <div className="w-full max-w-xs space-y-3">
          <button
            onClick={() => setView('form')}
            className="w-full py-4 bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-xl flex items-center justify-center gap-2 shadow-md transition-all active:scale-98"
            data-testid="join-rewards-btn"
          >
            <Gift className="w-5 h-5" />
            Join / Check Rewards
          </button>

          <button
            onClick={() => handleRedirect('order')}
            className="w-full py-4 bg-white hover:bg-gray-50 text-gray-800 font-semibold rounded-xl flex items-center justify-center gap-2 shadow border border-gray-200 transition-all active:scale-98"
            data-testid="order-online-btn"
          >
            <ShoppingBag className="w-5 h-5" />
            Order Online
          </button>

          <button
            onClick={() => handleRedirect('website')}
            className="w-full py-3 text-amber-600 hover:text-amber-700 font-medium flex items-center justify-center gap-1 transition-all"
            data-testid="continue-website-btn"
          >
            Continue to Website
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>

        {/* Footer */}
        <p className="mt-8 text-xs text-gray-400 text-center">
          Shop 3, Namaka Lane, Nadi, Fiji
        </p>
      </div>
    );
  }

  // Form View
  if (view === 'form') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-amber-50 to-orange-50 flex flex-col items-center p-4 pt-8" data-testid="wifi-form-page">
        {/* Logo */}
        <div className="mb-4">
          <img 
            src="/bulaccino-logo.svg" 
            alt="Bulaccino Cafe" 
            className="h-12 w-auto"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = '/logo.png';
            }}
          />
        </div>

        <h2 className="text-xl font-bold text-gray-900 mb-1">Join Bulaccino Rewards</h2>
        <p className="text-gray-500 text-sm mb-6">Enter your details to earn rewards</p>

        {error && (
          <div className="w-full max-w-sm bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="w-full max-w-sm space-y-4">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Your name"
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-base"
              required
              autoComplete="name"
              data-testid="wifi-name-input"
            />
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="your@email.com"
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-base"
              required
              autoComplete="email"
              inputMode="email"
              data-testid="wifi-email-input"
            />
          </div>

          {/* Phone */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              placeholder="+679 1234567"
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-base"
              required
              autoComplete="tel"
              inputMode="tel"
              data-testid="wifi-phone-input"
            />
          </div>

          {/* Consent Checkboxes */}
          <div className="space-y-3 pt-2">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.email_marketing_consent}
                onChange={(e) => setFormData({ ...formData, email_marketing_consent: e.target.checked })}
                className="w-5 h-5 mt-0.5 rounded border-gray-300 text-amber-500 focus:ring-amber-500"
                data-testid="wifi-email-consent"
              />
              <span className="text-sm text-gray-600">
                {settings.email_consent_text || 'Yes, send me special offers and updates via email'}
              </span>
            </label>

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.sms_marketing_consent}
                onChange={(e) => setFormData({ ...formData, sms_marketing_consent: e.target.checked })}
                className="w-5 h-5 mt-0.5 rounded border-gray-300 text-amber-500 focus:ring-amber-500"
                data-testid="wifi-sms-consent"
              />
              <span className="text-sm text-gray-600">
                {settings.sms_consent_text || 'Yes, send me exclusive deals via SMS'}
              </span>
            </label>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full py-4 bg-amber-500 hover:bg-amber-600 disabled:bg-amber-300 text-white font-semibold rounded-xl flex items-center justify-center gap-2 shadow-md transition-all mt-4"
            data-testid="wifi-submit-btn"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Joining...
              </>
            ) : (
              <>
                <Check className="w-5 h-5" />
                Join Rewards
              </>
            )}
          </button>

          {/* Back Button */}
          <button
            type="button"
            onClick={() => setView('welcome')}
            className="w-full py-2 text-gray-500 hover:text-gray-700 text-sm"
          >
            ← Back
          </button>

          {/* Privacy Link */}
          <p className="text-xs text-gray-400 text-center pt-2">
            By joining, you agree to our{' '}
            <a href="/privacy-policy" className="text-amber-600 underline" target="_blank">Privacy Policy</a>
            {' '}and{' '}
            <a href="/terms-of-service" className="text-amber-600 underline" target="_blank">Terms</a>
          </p>
        </form>
      </div>
    );
  }

  // Rewards View
  if (view === 'rewards') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-amber-50 to-orange-50 flex flex-col items-center p-4 pt-8" data-testid="wifi-rewards-page">
        {/* Success Check */}
        <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mb-4 shadow-lg">
          <Check className="w-8 h-8 text-white" />
        </div>

        <h2 className="text-xl font-bold text-gray-900 mb-1">
          {rewardsStatus?.is_existing_customer ? 'Welcome Back!' : 'Welcome to Bulaccino!'}
        </h2>
        <p className="text-gray-500 text-sm mb-6">{formData.name}</p>

        {/* Rewards Card */}
        <div className="w-full max-w-sm bg-white rounded-2xl shadow-lg overflow-hidden mb-6">
          {/* Tier Header */}
          <div className={`p-4 text-center text-white ${
            rewardsStatus?.loyalty_tier === 'Gold' ? 'bg-gradient-to-r from-yellow-500 to-amber-500' :
            rewardsStatus?.loyalty_tier === 'Silver' ? 'bg-gradient-to-r from-gray-400 to-gray-500' :
            'bg-gradient-to-r from-amber-600 to-orange-600'
          }`}>
            <Star className="w-6 h-6 mx-auto mb-1" />
            <p className="text-lg font-bold">{rewardsStatus?.loyalty_tier || 'Bronze'} Member</p>
            {rewardsStatus?.is_founding_member && (
              <span className="inline-block mt-1 px-2 py-0.5 bg-white/20 rounded-full text-xs">
                Founding Member
              </span>
            )}
          </div>

          {/* Stats */}
          <div className="p-4 space-y-3">
            {rewardsStatus?.is_coffee_pass_member && (
              <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg">
                <Coffee className="w-5 h-5 text-amber-600" />
                <div>
                  <p className="font-medium text-gray-900">Bula Coffee Pass Active</p>
                  <p className="text-xs text-gray-500">Free daily coffee 7-10am</p>
                </div>
              </div>
            )}

            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Points Balance</span>
              <span className="font-semibold text-gray-900">{rewardsStatus?.total_points || 0} pts</span>
            </div>

            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Lifetime Spend</span>
              <span className="font-semibold text-gray-900">${(rewardsStatus?.lifetime_spend || 0).toFixed(2)}</span>
            </div>

            {/* Available Rewards */}
            {rewardsStatus?.available_rewards?.length > 0 && (
              <div className="pt-2 border-t">
                <p className="text-sm font-medium text-gray-700 mb-2">Available Rewards</p>
                {rewardsStatus.available_rewards.map((reward, idx) => (
                  <div key={idx} className="flex items-center justify-between p-2 bg-green-50 rounded-lg mb-1">
                    <span className="text-sm text-green-800">{reward.name}</span>
                    <span className="text-xs text-green-600">{reward.points_required} pts</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="w-full max-w-sm space-y-3">
          <button
            onClick={() => handleRedirect('order')}
            className="w-full py-4 bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-xl flex items-center justify-center gap-2 shadow-md transition-all"
            data-testid="wifi-order-btn"
          >
            <ShoppingBag className="w-5 h-5" />
            Order Online Now
          </button>

          <button
            onClick={() => handleRedirect('rewards')}
            className="w-full py-3 bg-white hover:bg-gray-50 text-gray-800 font-medium rounded-xl flex items-center justify-center gap-2 shadow border border-gray-200 transition-all"
          >
            View Full Rewards
          </button>

          <button
            onClick={() => handleRedirect('website')}
            className="w-full py-2 text-gray-500 hover:text-gray-700 text-sm"
          >
            Continue to Website →
          </button>
        </div>

        {/* Enjoy WiFi Message */}
        <div className="mt-6 flex items-center gap-2 text-gray-500">
          <Wifi className="w-4 h-4" />
          <span className="text-sm">Enjoy your free Wi-Fi!</span>
        </div>
      </div>
    );
  }

  return null;
}
